/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
import { clsx } from '@accelint/design-foundation/lib/utils';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { ToggleButton } from 'react-aria-components/ToggleButton';
import styles from './styles.module.css';
import type { SidenavMenuItemProps } from './types';

/**
 * SidenavMenuItem - Menu item component for sidenav
 *
 * Provides a selectable item within a sidenav menu
 *
 * @example
 * ```tsx
 * <SidenavMenu icon={<Settings />} title="Settings">
 *   <SidenavMenuItem isSelected={true}>Profile</SidenavMenuItem>
 *   <SidenavMenuItem>Preferences</SidenavMenuItem>
 * </SidenavMenu>
 * ```
 *
 * @param props - {@link SidenavMenuItemProps}
 * @param props.className - CSS class for the menu item.
 * @param props.children - Menu item content.
 * @returns The rendered SidenavMenuItem component.
 */
export function SidenavMenuItem({
  className,
  children,
  ...rest
}: SidenavMenuItemProps) {
  return (
    <ToggleButton
      {...rest}
      className={composeRenderProps(className, (className) =>
        clsx(styles.item, className),
      )}
    >
      {children}
    </ToggleButton>
  );
}
